import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Fish, ArrowLeft, ArrowRight, Check, User, Mail, Lock, Phone, MapPin, Calendar } from "lucide-react";

const FISHING_TYPES = ["Fresh Water", "Specimen", "Bass", "Fly Fishing", "Rock and Surf", "Boat", "Offshore", "Estuary"];

const MEMBERSHIP_RULES = [
  "I will respect other members and maintain a positive community environment.",
  "I will provide accurate information about my catches.",
  "I understand my membership card is non-transferable.",
  "I agree to follow all local fishing regulations and conservation guidelines.",
  "I understand membership fees are non-refundable."
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    date_of_birth: "",
    email: "",
    password: "",
    confirmPassword: "",
    cell_number: "",
    area: "",
    fishing_types: [],
    agreed_to_rules: false
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleFishingType = (type) => {
    const current = formData.fishing_types;
    if (current.includes(type)) {
      setFormData({ ...formData, fishing_types: current.filter(t => t !== type) });
    } else {
      setFormData({ ...formData, fishing_types: [...current, type] });
    }
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.name || !formData.surname) {
          toast.error("Please enter your name and surname");
          return false;
        }
        return true;
      case 2:
        if (!formData.email || !formData.password || !formData.confirmPassword) {
          toast.error("Please fill in all fields");
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error("Passwords do not match");
          return false;
        }
        if (formData.password.length < 6) {
          toast.error("Password must be at least 6 characters");
          return false;
        }
        return true;
      case 3:
        if (!formData.cell_number || !formData.area) {
          toast.error("Please enter your contact details");
          return false;
        }
        return true;
      case 4:
        if (formData.fishing_types.length === 0) {
          toast.error("Please select at least one fishing type");
          return false;
        }
        return true;
      case 5:
        if (!formData.agreed_to_rules) {
          toast.error("Please agree to the membership rules");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    try {
      const userData = {
        name: formData.name,
        surname: formData.surname,
        email: formData.email,
        password: formData.password,
        cell_number: formData.cell_number,
        area: formData.area,
        fishing_types: formData.fishing_types,
        agreed_to_rules: formData.agreed_to_rules,
        date_of_birth: formData.date_of_birth || null
      };

      await register(userData);
      toast.success("Welcome to FishingFanatix!");
      navigate("/subscription");
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = [
    "Personal Info",
    "Account Setup",
    "Contact Details",
    "Fishing Interests",
    "Rules Agreement"
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-main)] flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[var(--river-stone)] hover:text-[var(--deep-ocean)] mb-8 transition"
          data-testid="back-to-home-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <Card className="bg-white card-shadow-deep border-0 overflow-hidden">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src="/logo.jpg" alt="FishingFanatix" className="h-16 w-auto" />
            </div>
            <CardTitle className="text-2xl text-[var(--deep-ocean)]">Join the Community</CardTitle>
            <CardDescription className="text-[var(--river-stone)]">
              Step {step} of 5: {stepTitles[step - 1]}
            </CardDescription>

            {/* Progress Bar */}
            <div className="flex gap-2 mt-4">
              {[1, 2, 3, 4, 5].map((s) => (
                <div
                  key={s}
                  className={`h-2 flex-1 rounded-full transition-all ${
                    s <= step ? "bg-[var(--deep-ocean)]" : "bg-gray-200"
                  }`}
                />
              ))}
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Personal Info */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <Label htmlFor="name">First Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="name"
                        name="name"
                        placeholder="John"
                        value={formData.name}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="surname">Surname</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="surname"
                        name="surname"
                        placeholder="Smith"
                        value={formData.surname}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="surname-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth (Optional)</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="date_of_birth"
                        name="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="dob-input"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Account Setup */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="password"
                        name="password"
                        type="password"
                        placeholder="At least 6 characters"
                        value={formData.password}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="password-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        placeholder="Repeat your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Contact Details */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <Label htmlFor="cell_number">Cell Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="cell_number"
                        name="cell_number"
                        placeholder="+27 82 123 4567"
                        value={formData.cell_number}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="cell-number-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="area">Area You Live In</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="area"
                        name="area"
                        placeholder="e.g., Cape Town, Durban, Johannesburg"
                        value={formData.area}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="area-input"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Fishing Interests */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <p className="text-[var(--river-stone)] text-sm">
                    Select all types of fishing you enjoy:
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {FISHING_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleFishingType(type)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          formData.fishing_types.includes(type)
                            ? "border-[var(--deep-ocean)] bg-[var(--deep-ocean)]/10"
                            : "border-gray-200 hover:border-[var(--deep-ocean)]/50"
                        }`}
                        data-testid={`fishing-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            formData.fishing_types.includes(type)
                              ? "border-[var(--deep-ocean)] bg-[var(--deep-ocean)]"
                              : "border-gray-300"
                          }`}>
                            {formData.fishing_types.includes(type) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <span className="text-sm font-medium text-[var(--deep-ocean)]">{type}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 5: Rules Agreement */}
              {step === 5 && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div className="bg-[var(--bg-main)] p-4 rounded-lg max-h-48 overflow-y-auto">
                    <h4 className="font-bold text-[var(--deep-ocean)] mb-3">Membership Rules</h4>
                    <ul className="space-y-3">
                      {MEMBERSHIP_RULES.map((rule, index) => (
                        <li key={index} className="flex gap-2 text-sm text-[var(--river-stone)]">
                          <span className="text-[var(--deep-ocean)] font-bold">{index + 1}.</span>
                          {rule}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="agreed_to_rules"
                      checked={formData.agreed_to_rules}
                      onCheckedChange={(checked) => setFormData({ ...formData, agreed_to_rules: checked })}
                      data-testid="agree-rules-checkbox"
                    />
                    <Label htmlFor="agreed_to_rules" className="text-sm text-[var(--river-stone)] cursor-pointer">
                      I have read and agree to the FishingFanatix membership rules and terms of service.
                    </Label>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-8">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  className="flex-1"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              {step < 5 ? (
                <Button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 btn-primary"
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmit}
                  className="flex-1 btn-accent"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? <span className="spinner mx-auto" /> : "Complete Registration"}
                </Button>
              )}
            </div>

            <div className="mt-6 text-center">
              <p className="text-[var(--river-stone)]">
                Already have an account?{" "}
                <Link 
                  to="/login" 
                  className="text-[var(--deep-ocean)] font-semibold hover:underline"
                  data-testid="login-link"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
